import {FC, useMemo, useRef, useCallback, useState, useEffect} from "react";
import {
	Box,
	LinearProgress,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	DataGridPro,
	GridColDef,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	useGridApiRef
} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {useNavigate} from "react-router-dom";
import {AdminTableLanguagesFilterSelect} from "@/components/common/table/AdminTableLanguagesFilterSelect";
import {
	useDeleteLesson,
	useGetI18nLocales,
	type ListLessonItemDto,
	DEFAULT_LESSONS_LIST_PAGE_SIZE,
	PAGE_SIZE_OPTIONS_COURSES_ADMIN
} from "@/app2/shared/data";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {convertSecondsToHHMMSS} from "@/utils";
import {useLessonsListGrid} from "./hooks/useLessonsListGrid";
import type {LessonsControllerListParams} from "@/app2/shared/data/gen";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";
import {LocalizationProvider} from "@mui/x-date-pickers";
import * as pkg from "@mui/x-date-pickers/AdapterDayjs/index.js";
const {AdapterDayjs} = pkg;

const useStyles = makeStyles()((theme) => ({
	dataGrid: {
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		},
		"& svg path": {
			fill: theme.palette.common.white
		}
	}
}));

const TABLE_KEY = "lessons-grid";
const INITIAL_COLUMN_VISIBILITY: Record<string, boolean> = {};

const defaultFilterSettings: LessonsControllerListParams = {
	page: 1,
	pageSize: DEFAULT_LESSONS_LIST_PAGE_SIZE,
	orderBy: "updatedAt",
	orderDirection: "desc",
	extendedAccess: false
};

export const LessonsListGrid: FC = () => {
	const {classes} = useStyles();
	const navigate = useNavigate();
	const deleteLesson = useDeleteLesson();
	const {successSnackbar} = useCustomSnackbar();
	const buttonContainerRef = useRef<HTMLDivElement>(null);
	const apiRef = useGridApiRef();

	const {
		const: {filterModel},
		api: {
			assignFilters,
			getPersistedFilterModel,
			handleFilterModelChange
		}
	} = useCustomDataGrid<ListLessonItemDto>({
		defaultFilterSettings,
		filterStorageKey: "lessonsFilterSettings"
	});
	const initialFilterSettings = getPersistedFilterModel() as LessonsControllerListParams;
	const {
		rows,
		rowCount,
		isLoading,
		isFetched,
		refetch,
		page,
		pageSize,
		sortModel,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	} = useLessonsListGrid({
		initialFilterSettings,
		onFilterModelChange: handleFilterModelChange
	});

	// Column visibility (persisted to localStorage)
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() => {
		const saved = localStorage.getItem(`${TABLE_KEY}-visibility`);
		return saved ? JSON.parse(saved) : INITIAL_COLUMN_VISIBILITY;
	});

	const [layoutModified, setLayoutModified] = useState(false);

	const handleColumnVisibilityChange = useCallback((newModel: Record<string, boolean>) => {
		setColumnVisibilityModel(newModel);
		setLayoutModified(true);
		localStorage.setItem(`${TABLE_KEY}-visibility`, JSON.stringify(newModel));
	}, []);

	// Subscribe to column order and width changes
	useEffect(() => {
		const unsubscribeOrder = apiRef.current.subscribeEvent("columnOrderChange", () => {
			const orderedFields = apiRef.current.getAllColumns().map(col => col.field);
			localStorage.setItem(`${TABLE_KEY}-order`, JSON.stringify(orderedFields));
			setLayoutModified(true);
		});

		const unsubscribeWidth = apiRef.current.subscribeEvent("columnWidthChange", () => {
			const columnWidths: Record<string, number> = {};
			apiRef.current.getAllColumns().forEach(col => {
				if (col.field) {
					columnWidths[col.field] = col.computedWidth || col.width || 100;
				}
			});
			localStorage.setItem(`${TABLE_KEY}-widths`, JSON.stringify(columnWidths));
			setLayoutModified(true);
		});

		if (
			localStorage.getItem(`${TABLE_KEY}-order`) ||
			localStorage.getItem(`${TABLE_KEY}-widths`) ||
			localStorage.getItem(`${TABLE_KEY}-visibility`)
		) {
			setLayoutModified(true);
		}

		return () => {
			unsubscribeOrder();
			unsubscribeWidth();
		};
	}, [apiRef]);

	const resetLayout = useCallback(() => {
		localStorage.removeItem(`${TABLE_KEY}-order`);
		localStorage.removeItem(`${TABLE_KEY}-widths`);
		localStorage.removeItem(`${TABLE_KEY}-visibility`);
		setLayoutModified(false);
		setColumnVisibilityModel(INITIAL_COLUMN_VISIBILITY);
	}, []);

	const data = useMemo(
		() => (isFetched && !isLoading ? rows : []),
		[isFetched, isLoading, rows]
	);

	const {data: localesData} = useGetI18nLocales();
	const locales = localesData?.data ?? [];

	const [deleteTargetState, setDeleteTargetState] = useState<ListLessonItemDto | null>(null);

	const onRowClick = useCallback(
		(params: { id: number }, event: React.MouseEvent) => {
			if (buttonContainerRef.current?.contains(event.target as Node)) return;
			navigate(`${params.id}`);
		},
		[navigate]
	);

	const handleDeleteClick = useCallback((e: React.MouseEvent, row: ListLessonItemDto) => {
		e.stopPropagation();
		setDeleteTargetState(row);
	}, []);

	const handleDeleteConfirm = useCallback(async () => {
		if (!deleteTargetState) return;
		try {
			await deleteLesson.mutateAsync({lessonId: deleteTargetState.id});
			setDeleteTargetState(null);
			refetch();
			successSnackbar("Lesson deleted");
		} catch (err) {
			console.error("Delete lesson failed", err);
		}
	}, [deleteTargetState, deleteLesson, refetch, successSnackbar]);

	const handleDeleteCancel = useCallback(() => {
		setDeleteTargetState(null);
	}, []);

	const columns: GridColDef<ListLessonItemDto>[] = useMemo(
		() => [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				type: "string",
				hideable: false
			},
			{
				field: "title",
				type: "string",
				flex: 1,
				headerName: "Title",
				minWidth: 240
			},
			{
				field: "videoDuration",
				type: "number",
				headerAlign: "left",
				align: "left",
				flex: 1,
				headerName: "Duration",
				valueGetter: (params: any) =>
					params.row.videoDuration != null
						? convertSecondsToHHMMSS(params.row.videoDuration)
						: ""
			},
			{
				field: "updatedAt",
				type: "date",
				flex: 1,
				headerName: "Last Modified",
				valueGetter: (params: any) =>
					params.row.updatedAt ? new Date(params.row.updatedAt) : null,
				valueFormatter: (params: any) =>
					formatDateForDisplay(params.value as Date | null)
			},
			{
				field: "publishedAt",
				type: "date",
				flex: 1,
				headerName: "Published",
				valueGetter: (params: any) =>
					params.row.publishedAt ? new Date(params.row.publishedAt) : null,
				valueFormatter: (params: any) =>
					formatDateForDisplay(params.value as Date | null)
			},
			{
				field: "languages",
				type: "string",
				filterable: true,
				sortable: false,
				flex: 1,
				headerName: "Supported Languages",
				valueGetter: (params) => {
					const codes = (params.row as ListLessonItemDto & {languages?: string[]}).languages;
					if (!codes?.length) return "";
					return codes
						.map((code) =>
							(locales as {code?: string; name?: string}[]).find((l) => l.code === code)?.name ?? code
						)
						.join(", ");
				},
				filterOperators: [
					{
						label: "contains",
						value: "co",
						InputComponent: AdminTableLanguagesFilterSelect,
						getApplyFilterFn: () => null
					}
				]
			},
			{
				field: "actions",
				headerName: "",
				width: 56,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderCell: (params) => (
					<Box ref={buttonContainerRef} onClick={(e) => e.stopPropagation()}>
						<IconButton
							size="small"
							aria-label="Delete lesson"
							onClick={(e) => handleDeleteClick(e, params.row)}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Box>
				)
			}
		],
		[handleDeleteClick, locales]
	);

	// Apply saved column order and widths
	const storedColumnOrder: string[] = JSON.parse(localStorage.getItem(`${TABLE_KEY}-order`) || "[]");
	const storedColumnWidths: Record<string, number> = JSON.parse(localStorage.getItem(`${TABLE_KEY}-widths`) || "{}");

	const orderedColumns = storedColumnOrder.length
		? storedColumnOrder.map(field => columns.find(col => col.field === field)).filter(Boolean) as GridColDef<ListLessonItemDto>[]
		: columns;

	const finalColumns = orderedColumns.map((col) => ({
		...col,
		width: storedColumnWidths[col.field] || col.width,
		flex: storedColumnWidths[col.field] ? undefined : col.flex
	}));

	const Toolbar = useCallback(() => (
		<GridToolbarContainer sx={{pt: 0, pb: 1, px: 2, alignItems: "center"}}>
			<GridToolbarColumnsButton {...({} as any)} />
			<GridToolbarFilterButton {...({} as any)} />
			{layoutModified && (
				<Button size="small" onClick={resetLayout}>
					Reset Layout
				</Button>
			)}
		</GridToolbarContainer>
	), [layoutModified, resetLayout]);

	return (
		<>
			<Box>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DataGridPro<ListLessonItemDto>
					apiRef={apiRef}
					autoHeight
					className={classes.dataGrid}
					loading={isLoading}
					sortingMode="server"
					page={page}
					pageSize={pageSize}
					onPageSizeChange={onPageSizeChange}
					onPageChange={onPageChange}
					checkboxSelection
					paginationMode="server"
					pagination
					rowCount={rowCount}
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					rows={data}
					columns={finalColumns.map((e) => assignFilters(e as any))}
					filterMode="server"
					filterModel={filterModel}
					onFilterModelChange={onFilterChange}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={handleColumnVisibilityChange}
					components={{Toolbar}}
					rowsPerPageOptions={[...PAGE_SIZE_OPTIONS_COURSES_ADMIN]}
					initialState={defaultInitialState}
					onRowClick={(params, event) => onRowClick({id: params.id as number}, event)}
					getRowId={(row) => row.id}
					disableSelectionOnClick
					sx={{
						"& .MuiDataGrid-row": {
							cursor: "pointer"
						}
					}}
				/>
				</LocalizationProvider>
				{isLoading && <LinearProgress />}
			</Box>
			<Dialog open={!!deleteTargetState} onClose={handleDeleteCancel}>
				<DialogTitle>Delete lesson</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{color: "grey.800"}}>
						{deleteTargetState?.enrollmentCount && deleteTargetState.enrollmentCount >= 1
							? "You will be making an assigned lesson unavailable. Are you sure you want to delete this lesson?"
							: "Are you sure you want to delete this lesson?"}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel}>Cancel</Button>
					<Button
						onClick={handleDeleteConfirm}
						color="error"
						variant="contained"
						disabled={deleteLesson.isPending}
					>
						{deleteLesson.isPending ? "Deleting…" : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
